import { useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from '../../../../../redux/store';

import ApproveInvoice from '../ApproveInvoice';
import SubmitApprovers from './SubmitApprovers';
import SubmitForReview from './SubmitForReview';
import SubmitInvoice from './SubmitInvoice';
import { useWithDuplicateCheck } from '../reusable/hooks/useWithDuplicateCheck';
import { DuplicateInvoicePopup } from '../RelatedInvoices';

Submit.propTypes = {
  glRows: PropTypes.array,
  unsavedData: PropTypes.object,
  setUnsavedData: PropTypes.func,
  selectedApprovers: PropTypes.any,
  editingApprovers: PropTypes.bool,
};
export default function Submit({ glRows, unsavedData, setUnsavedData, selectedApprovers, editingApprovers }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const section = location.pathname.split('/')[3];
  const {
    data: { invoiceApprovers },
  } = useSelector((state) => state.invoicing);
  const { user } = useSelector((state) => state.user);

  const invoicesInQueue = JSON.parse(localStorage.getItem('selectedInvoices') || '[]');
  const invoicesLeft = invoicesInQueue?.length;
  const numericId = Number(id);
  const currentIndex = invoicesInQueue?.indexOf(numericId);
  const { withDuplicateCheck, showDuplicatePopup, pendingSubmitLabel, handleSubmitAnyway, handleClose } =
    useWithDuplicateCheck();

  const removeCurrentInvoice = () => {
    if (invoicesLeft <= 1) {
      localStorage.removeItem('selectedInvoices');
      navigate(`/dashboard/invoicing/${section}`);
    } else if (currentIndex === invoicesLeft - 1) {
      const prevId = invoicesInQueue[currentIndex - 1];
      localStorage.setItem('selectedInvoices', JSON.stringify(invoicesInQueue.slice(0, -1) || []));
      navigate(`/dashboard/invoicing/${section}/${prevId}`);
    } else {
      const nextId = invoicesInQueue[currentIndex + 1];
      localStorage.setItem('selectedInvoices', JSON.stringify(invoicesInQueue.slice(1) || []));
      navigate(`/dashboard/invoicing/${section}/${nextId}`);
    }
  };

  useEffect(() => {
    setUnsavedData({});
  }, [id]);

  const approvalRequired =
    invoiceApprovers?.some(
      (x) => x.approverEmail?.toLowerCase() === user.email?.toLowerCase() && x.status === 1 // status=1: pending
    ) ?? false;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {section !== 'new' ? (
        <>
          {approvalRequired ? (
            <ApproveInvoice goToNext={removeCurrentInvoice} />
          ) : (
            <SubmitInvoice
              glRows={glRows}
              unsavedData={unsavedData}
              setUnsavedData={setUnsavedData}
              goToNext={removeCurrentInvoice}
              withDuplicateCheck={withDuplicateCheck}
            />
          )}
        </>
      ) : (
        //section===new
        <>
          {editingApprovers || selectedApprovers?.length > 0 ? (
            <SubmitApprovers
              glRows={glRows}
              selectedApprovers={selectedApprovers}
              goToNext={removeCurrentInvoice}
              withDuplicateCheck={withDuplicateCheck}
            />
          ) : (
            <>
              <SubmitForReview
                unsavedData={unsavedData}
                setUnsavedData={setUnsavedData}
                glRows={glRows}
                goToNext={removeCurrentInvoice}
                withDuplicateCheck={withDuplicateCheck}
              />
              <SubmitInvoice
                glRows={glRows}
                unsavedData={unsavedData}
                setUnsavedData={setUnsavedData}
                goToNext={removeCurrentInvoice}
                buttonVariant="outlined"
                buttonText="Submit Now"
                withDuplicateCheck={withDuplicateCheck}
              />
            </>
          )}
        </>
      )}
      <DuplicateInvoicePopup
        open={showDuplicatePopup}
        onClose={handleClose}
        onSubmitAnyway={handleSubmitAnyway}
        submitLabel={pendingSubmitLabel}
      />
    </Box>
  );
}
